import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { last, unique } from 'lib/array/arrayUtils'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'
import { hasAnyIncompletePassportDetails } from 'lib/flights/flightSummaryHelpers'

export default function useFlightPurchaseDetails(order: App.Order) {
  const intl = useIntl()
  const {
    status,
    flightItems,
  } = order

  const allItemIds = useMemo(() => order.flightItems.map(item => item.id), [order.flightItems])

  const allFlightDetails = useOrderFlightDetails(order.id, allItemIds)

  const firstFlight = allFlightDetails[0]?.flightDetails
  const lastFlight = allItemIds.length > 1 ? allFlightDetails[allFlightDetails.length - 1]?.flightDetails : undefined
  const fetching = allItemIds.length > 1
    ? (allFlightDetails[0]?.fetching && allFlightDetails[allFlightDetails.length - 1]?.fetching)
    : allFlightDetails[0]?.fetching

  const legs = useMemo(() => {
    if (firstFlight && lastFlight) {
      return [...firstFlight.bookingJourney.legs, ...lastFlight.bookingJourney.legs]
    } else if (firstFlight) {
      return firstFlight.bookingJourney.legs
    }
    return []
  }, [firstFlight, lastFlight])

  const tripType = useMemo(() => {
    if (legs.length > 2) {
      return 'MultiCity'
    }
    if (legs.length === 2) {
      const isOpenJaw = last(legs[0].flights).arrivalAirport !== legs[1].flights[0].departureAirport
      const isReverseOpenJaw = last(legs[1].flights).arrivalAirport !== legs[0].flights[0].departureAirport
      if (isOpenJaw || isReverseOpenJaw) {
        // Open jaw flights are considered to be Multi-City
        return 'MultiCity'
      }
    }

    if (firstFlight && lastFlight) {
      return 'TwoOneWay'
    }

    if (legs.length === 2 && firstFlight && !lastFlight) {
      return 'Return'
    }

    if (legs.length === 1) {
      return 'OneWay'
    }

    return ''
  }, [legs, firstFlight, lastFlight])

  // firstly try to use a collection of leg PNRs
  // - These have to be deduped as flights with multiple legs from the same airline have the same PNR
  let pnrIds = Array.from(new Set(legs.map(leg => leg.pnr).filter(pnr => !!pnr)))

  // fallback to using the pnrId on the flightItems
  if (!pnrIds || pnrIds.length === 0) {
    pnrIds = flightItems.map(flightItem => flightItem.pnrId)
  }

  const noOfAdults = useMemo(() => legs[0]?.paxCount.adult, [legs])
  const noOfChildren = useMemo(() => legs[0]?.paxCount.child, [legs])
  const noOfInfants = useMemo(() => legs[0]?.paxCount.infant, [legs])
  const departingFlight = legs[0]?.flights[0]
  const arrivalFlight = tripType === 'OneWay' ? last(legs[0]?.flights) : last(legs)?.flights[0]
  const arrivalFlightLabel = tripType === 'OneWay' ? arrivalFlight?.arrivalAirportName + ', ' + arrivalFlight?.arrivalCountry : arrivalFlight?.departureAirportName + ', ' + arrivalFlight?.departureCountry
  const departingDate = departingFlight?.departingDate
  const arrivalDate = arrivalFlight?.arrivalDate ?? departingDate

  const carrierLogos = useMemo(() => {
    const urls = unique(legs.map(leg => leg.flights[0].marketingAirline.logoUrl))

    if (urls.length > 1) {
      return unique(legs.map(leg => leg.flights[0].marketingAirline.brandMarkUrl))
    }

    return urls
  }, [legs])

  const flightsName = useMemo(() => {
    if (tripType === 'MultiCity') {
      const uniqueCities = new Set()
      legs.forEach(leg => {
        uniqueCities.add(leg.flights[0].departureCity)
        uniqueCities.add(last(leg.flights).arrivalCity)
      })
      const cities: Array<string> = Array.from(uniqueCities) as Array<string>
      return intl.formatMessage(
        {
          id: 'ZNbEqXNoEsepsr0dFlsM',
          defaultMessage: 'Flights to {cities} & {lastCity}',
          description: 'Multi-city flight name showing all destination cities',
        },
        {
          cities: cities.slice(0, -1).join(', '),
          lastCity: cities.slice(-1)[0],
        },
      )
    }

    if (departingFlight && arrivalFlight) {
      return intl.formatMessage(
        {
          id: 'oGbtqb3BU5Jz5YTyD8OU',
          defaultMessage: '{departureAirport}, {departureCountry} to {arrivalLabel}',
          description: 'Flight route name showing departure and arrival airports',
        },
        {
          departureAirport: departingFlight.departureAirportName,
          departureCountry: departingFlight.departureCountry,
          arrivalLabel: arrivalFlightLabel,
        },
      )
    }

    return undefined
  }, [tripType, departingFlight, arrivalFlight, arrivalFlightLabel, legs, intl])

  const flightTypeLabel = useMemo(() => {
    if (tripType === 'MultiCity') {
      return intl.formatMessage({
        id: 'TCfq2bi7NJN-vD43I_aJ',
        defaultMessage: 'Multi-city flights',
        description: 'Flight type label for multi-city flight bookings on the order summary page',
      })
    }

    return tripType === 'OneWay'
      ? intl.formatMessage({
          id: 'xmWEb-PfvzBF0B8o24Jq',
          defaultMessage: 'One-way Flight',
          description: 'Flight type label for one-way flight bookings on the order summary page',
        })
      : intl.formatMessage({
          id: 'SYzqF1l_JIK2YU2HtpQW',
          defaultMessage: 'Return Flights',
          description: 'Flight type label for return flight bookings on the order summary page',
        })
  }, [tripType, intl])

  const hasIncompletePassportDetails = useMemo(
    () => hasAnyIncompletePassportDetails(allFlightDetails),
    [allFlightDetails],
  )

  return {
    fetching,
    flightsName,
    departingDate,
    arrivalDate,
    noOfAdults,
    noOfChildren,
    noOfInfants,
    departingFlight,
    arrivalFlight,
    arrivalFlightLabel,
    flightTypeLabel,
    carrierLogos,
    pnrIds,
    status,
    hasIncompletePassportDetails,
  }
}
